import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { confirmForgotPasswordReset } from '../../services/api';

type LocationState = { verifiedToken?: string; otp?: string; requestToken?: string };

export default function ForgotPasswordResetPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifiedToken = '', otp = '', requestToken = '' } = (location.state as LocationState | null) ?? {};

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  if (!verifiedToken || !otp) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 px-4 py-8">
        <div className="mx-auto w-full max-w-md rounded-2xl border border-orange-100 bg-white p-6 text-center shadow-xl">
          <p className="text-sm text-gray-600">Please complete the previous steps first.</p>
          <Link to="/forgot-password" className="mt-4 inline-block text-sm font-medium text-orange-700 hover:underline">
            Start over
          </Link>
        </div>
      </div>
    );
  }

  const onConfirm = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Please enter new password and confirm password');
      return;
    }
    setLoading(true);
    try {
      const res = await confirmForgotPasswordReset({
        token: verifiedToken,
        otp,
        newPassword,
        confirmPassword,
      });
      if (!res.success) {
        toast.error(res.message || 'Failed to reset password');
        return;
      }
      toast.success('Password reset successfully');
      navigate('/login');
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to reset password';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 px-4 py-8">
      <div className="mx-auto w-full max-w-md rounded-2xl border border-orange-100 bg-white p-6 shadow-xl shadow-orange-100/50">
        <p className="text-center text-xs font-medium uppercase tracking-wide text-orange-600">
          Step 3 of 3
        </p>
        <h1 className="mt-1 text-center text-xl font-bold text-gray-900">New password</h1>
        <p className="mt-2 text-center text-sm text-gray-600">
          Choose a strong password. It must include upper and lower case, a number, and a special character.
        </p>

        <div className="mt-6 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">New password</label>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
              placeholder="New password"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Confirm password</label>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
              placeholder="Confirm password"
            />
          </div>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="w-full rounded-lg bg-orange-600 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60"
          >
            {loading ? 'Saving…' : 'Confirm'}
          </button>
        </div>

        <div className="mt-6 flex flex-col gap-2 text-center text-sm">
          {requestToken ? (
            <button
              type="button"
              onClick={() => navigate('/forgot-password/otp', { state: { requestToken } })}
              className="text-orange-700 hover:underline"
            >
              Back to step 2
            </button>
          ) : (
            <Link to="/forgot-password" className="text-orange-700 hover:underline">
              Start over
            </Link>
          )}
          <Link to="/login" className="text-gray-500 hover:underline">
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
